import { AlertCircle, Pause, Play, RotateCcw, RotateCw, Volume2, VolumeX } from 'lucide-react'
import { IconButton, Tooltip } from '@/components/ui'
import { cn } from '@/internationalization/cn'
import { formatTimecode } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import { usePlayer } from './PlayerContext'
import { SKIP_SECONDS } from './MeetingPlayer'

/**
 * The visible controls for `MeetingPlayer`.
 *
 * Separate from the provider so the element can wrap the whole page (playback
 * has to survive a tab change) while the bar renders where it belongs — under
 * the meeting header, above the tabs.
 *
 * IT RENDERS ITS FAILURES AS SENTENCES, not as a disabled play button. "No
 * audio is stored for this meeting yet" and "playback is not enabled on this
 * deployment" send two different people to two different places; a greyed-out
 * control sends both of them to ask somebody.
 */
export function MeetingPlayerBar() {
  const t = useT()
  const player = usePlayer()
  if (player === null) return null

  const { state, currentTime, duration, isPlaying, muted, speed } = player

  if (state !== 'ready' && state !== 'loading') {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm text-fg-muted">
        <AlertCircle aria-hidden className="h-4 w-4 shrink-0 text-fg-subtle" />
        <span className="min-w-0 flex-1">
          {state === 'unavailable' && t('player.unavailable')}
          {state === 'not-configured' && t('player.notConfigured')}
          {state === 'media-error' && t('player.mediaError')}
          {state === 'forbidden' && t('player.forbidden')}
          {state === 'failed' && t('player.failed')}
        </span>
        {(state === 'media-error' || state === 'failed') && (
          <button
            type="button"
            onClick={() => void player.retry()}
            className="shrink-0 text-sm font-medium text-brand hover:underline"
          >
            {t('player.retry')}
          </button>
        )}
      </div>
    )
  }

  const loading = state === 'loading'
  // Before metadata arrives there is no duration, so the scrubber has nothing
  // to be a proportion OF. A zero-width bar would read as a recording of
  // length zero; a disabled full-width one reads as not ready yet.
  const max = duration ?? 0
  const progress = max > 0 ? Math.min(100, (currentTime / max) * 100) : 0

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-surface px-3 py-2">
      <IconButton
        label={isPlaying ? t('player.pause') : t('player.play')}
        onClick={player.toggle}
        disabled={loading}
        variant="secondary"
        icon={isPlaying ? <Pause /> : <Play />}
      />

      <div className="flex shrink-0 items-center gap-1">
        <IconButton
          label={t('player.back', { seconds: SKIP_SECONDS })}
          onClick={() => player.skip(-SKIP_SECONDS)}
          disabled={loading}
          size="sm"
          icon={<RotateCcw />}
        />
        <IconButton
          label={t('player.forward', { seconds: SKIP_SECONDS })}
          onClick={() => player.skip(SKIP_SECONDS)}
          disabled={loading}
          size="sm"
          icon={<RotateCw />}
        />
      </div>

      <span className="shrink-0 font-mono text-xs tabular-nums text-fg-subtle">
        {formatTimecode(currentTime)} / {duration === null ? '--:--' : formatTimecode(duration)}
      </span>

      {/* `focus-within` on the wrapper is what gives the hidden range input a
          visible focus ring: the element that actually receives focus is
          `opacity-0`, so without this a keyboard user tabbing here gets no
          indication at all (WCAG 2.4.7). */}
      <div className="relative min-w-[8rem] flex-1 rounded-full focus-within:ring-2
        focus-within:ring-brand focus-within:ring-offset-2 focus-within:ring-offset-surface">
        <div aria-hidden className="h-1.5 w-full overflow-hidden rounded-full bg-surface-muted">
          <div className="h-full rounded-full bg-brand" style={{ width: `${progress}%` }} />
        </div>
        {/* A range input rather than a click-to-position div: keyboard
            operable and screen-reader announced for free, both of which a
            bare div would need re-implementing and would get subtly wrong. */}
        <input
          type="range"
          min={0}
          max={max || 1}
          // Five seconds per arrow press. Half a second meant 120 presses to
          // cross a minute of recording, which is not a usable control for
          // anybody driving it from the keyboard.
          step={5}
          value={Math.min(currentTime, max || 1)}
          disabled={loading || max === 0}
          onChange={(event) => player.seek(Number(event.target.value))}
          aria-label={t('player.scrub')}
          // Without this a screen reader announces the raw number of seconds
          // ("1247") rather than a position in the recording.
          aria-valuetext={`${formatTimecode(currentTime)} of ${
            duration === null ? '' : formatTimecode(duration)
          }`}
          className={cn(
            'absolute inset-0 h-full w-full cursor-pointer opacity-0',
            (loading || max === 0) && 'cursor-not-allowed',
          )}
        />
      </div>

      <Tooltip content={t('player.speedTip')}>
        <button
          type="button"
          onClick={player.cycleSpeed}
          disabled={loading}
          className="shrink-0 rounded-md px-2 py-1 font-mono text-xs tabular-nums text-fg-muted
            hover:bg-surface-muted disabled:opacity-50"
        >
          {speed}×
        </button>
      </Tooltip>

      <IconButton
        label={muted ? t('player.unmute') : t('player.mute')}
        onClick={player.toggleMute}
        disabled={loading}
        size="sm"
        icon={muted ? <VolumeX /> : <Volume2 />}
      />
    </div>
  )
}
